// InferredFieldsBuilder.jsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import { FaInfoCircle } from "react-icons/fa";

const FIELD_TYPES = [
  "text", "email", "tel", "number", "date", "time", "datetime", "boolean", "url", "json",
  // Library-only types
  "select", "radio", "checkbox", "toggle", "range", "textarea", "datetime-local",
];

import styles from "./InferredFieldsBuilder.module.css";

import {
  LibraryAttachmentStrip,
  PinnedSnapshotSummary,
} from "./utils/uiFieldComponentsShared";

import ValidationRulesBuilder from "./ValidationRulesBuilder";
import {
  fetchRulesForForm,
  saveRulesForForm,
} from "./utils/validationRulesApi";

const TYPE_HINTS = {
  text: "Free-form text input",
  email: "Email address with validation",
  tel: "Phone number with validation",
  number: "Numeric input (integers or decimals)",
  date: "Calendar date picker",
  time: "Time picker",
  datetime: "Date + time picker",
  boolean: "Yes / No checkbox",
  url: "Web URL with validation",
  json: "Raw JSON editor — use only for structured payloads",
  select: "Dropdown — user picks one of the listed options",
  radio: "Radio buttons — user picks one of the listed options",
  checkbox: "Checkboxes — user picks any combination of the options",
  toggle: "Yes / No switch",
  range: "Numeric slider bounded by min and max",
  textarea: "Multi-line free-form text",
  "datetime-local": "Date + time picker (locale-formatted)",
};

const FORMATS_BY_TYPE = {
  date: [
    { value: "YYYY-MM-DD", label: "YYYY-MM-DD (2026-10-02)" },
    { value: "MM/DD/YYYY", label: "MM/DD/YYYY (10/02/2026)" },
    { value: "DD/MM/YYYY", label: "DD/MM/YYYY (02/10/2026)" },
    { value: "MM-DD-YYYY", label: "MM-DD-YYYY (10-02-2026)" },
    { value: "DD-MM-YYYY", label: "DD-MM-YYYY (02-10-2026)" },
    { value: "DD.MM.YYYY", label: "DD.MM.YYYY (02.10.2026)" },
    { value: "D MMM YYYY", label: "D MMM YYYY (2 Oct 2026)" },
    { value: "D MMMM YYYY", label: "D MMMM YYYY (2 October 2026)" },
  ],
  time: [
    { value: "HH:mm", label: "HH:mm (14:30)" },
    { value: "HH:mm:ss", label: "HH:mm:ss (14:30:00)" },
    { value: "hh:mm A", label: "hh:mm A (02:30 PM)" },
  ],
  datetime: [
    { value: "YYYY-MM-DDTHH:mm:ssZ", label: "ISO 8601 (2026-10-02T14:30:00Z)" },
    { value: "YYYY-MM-DD HH:mm:ss", label: "YYYY-MM-DD HH:mm:ss" },
    { value: "MM/DD/YYYY HH:mm", label: "MM/DD/YYYY HH:mm" },
  ],
  number: [
    { value: "integer", label: "Integer (2, 42, -7)" },
    { value: "decimal", label: "Decimal (3.14, 2.71828)" },
    { value: "decimal-2", label: "Decimal, 2 places (3.14, 2.70)" },
  ],
  boolean: [
    { value: "true_false", label: "true / false" },
    { value: "one_zero", label: "1 / 0" },
    { value: "yes_no", label: "yes / no" },
  ],
  range: [
    { value: "integer", label: "Integer (2, 42, -7)" },
    { value: "decimal", label: "Decimal (3.14, 2.71828)" },
    { value: "decimal-2", label: "Decimal, 2 places (3.14, 2.70)" },
  ],
  toggle: [
    { value: "true_false", label: "true / false" },
    { value: "one_zero", label: "1 / 0" },
    { value: "yes_no", label: "yes / no" },
  ],
  "datetime-local": [
    { value: "YYYY-MM-DDTHH:mm:ssZ", label: "ISO 8601 (2026-10-02T14:30:00Z)" },
    { value: "YYYY-MM-DD HH:mm:ss", label: "YYYY-MM-DD HH:mm:ss" },
    { value: "MM/DD/YYYY HH:mm", label: "MM/DD/YYYY HH:mm" },
  ],
  select: [
    { value: "string", label: "String (as the option value is typed)" },
    { value: "integer", label: "Integer (e.g. \"1\" → 1)" },
    { value: "number", label: "Number (e.g. \"1.5\" → 1.5)" },
    { value: "boolean", label: "Boolean (yes / true / 1 → true)" },
  ],
  radio: [
    { value: "string", label: "String (as the option value is typed)" },
    { value: "integer", label: "Integer (e.g. \"1\" → 1)" },
    { value: "number", label: "Number (e.g. \"1.5\" → 1.5)" },
    { value: "boolean", label: "Boolean (yes / true / 1 → true)" },
  ],
  checkbox: [
    { value: "string", label: "String (each option value as typed)" },
    { value: "integer", label: "Integer (each option value → int)" },
    { value: "number", label: "Number (each option value → float)" },
    { value: "boolean", label: "Boolean (each option value → bool)" },
  ],
};

const formatOptionsForType = (type) => FORMATS_BY_TYPE[type] || null;

const defaultFormatForType = (type) => {
  const options = formatOptionsForType(type);
  return options && options.length > 0 ? options[0].value : null;
};

const prettyTypeName = (t) => (t === "datetime-local" ? "datetime" : t);

const FORMAT_TOOLTIP_INTRO = (
  <>
    <strong style={{ display: "block", marginBottom: "4px", color: "#90cdf4" }}>Why field format matters</strong>
    The format tells the client the exact string shape your API expects for this field. Even when the type is correct, if the format is wrong the value will arrive in the wrong shape and the API will reject it.<br /><br />Example: an API expecting <code style={{ color: "#f6ad55" }}>02/10/2026</code> will reject <code style={{ color: "#f6ad55" }}>2026-10-02</code>. Pick the format that matches the sample value in the API configuration.
  </>
);

const InferredFieldsBuilder = ({
  fields,
  onFieldsChange,
  isConfigured,
  readOnly = true,
  library = [],
  libraryLoading = false,
  intentName,
  onOpenPicker,
  onReimportFromLibrary,
  onDetachFromLibrary,
  onRequestPinToggle,
  formUuid = null,
  appName = null,
  region = null,
}) => {
  const [showTypeHelp, setShowTypeHelp] = useState(false);
  const [showFormatHelp, setShowFormatHelp] = useState(false);
  const [showWidgetHelp, setShowWidgetHelp] = useState(false);

  const [validationRules, setValidationRules] = useState([]);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [rulesLoadedForFormUuid, setRulesLoadedForFormUuid] = useState(null);
  const rulesSaveTimerRef = useRef(null);

  const hasLibraryFeature =
    typeof onOpenPicker === "function" &&
    typeof onReimportFromLibrary === "function" &&
    typeof onDetachFromLibrary === "function" &&
    typeof onRequestPinToggle === "function";

  useEffect(() => {
    if (!formUuid || !appName) {
      setValidationRules([]);
      setRulesLoadedForFormUuid(null);
      return;
    }
    if (rulesLoadedForFormUuid === formUuid) return;
    let cancelled = false;
    setRulesLoading(true);
    (async () => {
      const rules = await fetchRulesForForm({ appName, formUuid, region });
      if (cancelled) return;
      setValidationRules(rules);
      setRulesLoadedForFormUuid(formUuid);
      setRulesLoading(false);
    })();
    return () => { cancelled = true; };
  }, [formUuid, appName, region, rulesLoadedForFormUuid]);

  useEffect(() => {
    return () => {
      if (rulesSaveTimerRef.current) {
        clearTimeout(rulesSaveTimerRef.current);
        rulesSaveTimerRef.current = null;
      }
    };
  }, [formUuid]);

  const handleRulesChange = useCallback(
    (nextRules) => {
      setValidationRules(nextRules);
      if (!formUuid || !appName) return;
      if (rulesSaveTimerRef.current) clearTimeout(rulesSaveTimerRef.current);
      rulesSaveTimerRef.current = setTimeout(async () => {
        rulesSaveTimerRef.current = null;
        try {
          await saveRulesForForm({ appName, formUuid, rules: nextRules, region });
        } catch (err) {
          console.error("[InferredFieldsBuilder] saveRulesForForm failed:", err);
          alert(`Could not save validation rules: ${err.message}`);
        }
      }, 1500);
    },
    [formUuid, appName, region],
  );

  const handleFieldChange = (index, key, value) => {
    const updated = fields.map((f, i) => {
      if (i !== index) return f;
      return { ...f, [key]: value };
    });
    onFieldsChange(updated);
  };

  const handleTypeChange = (index, newType) => {
    const current = fields[index];
    if (current?.component_id) return;
    const newFormatOptions = formatOptionsForType(newType);
    const patch = { type: newType };
    if (newFormatOptions && newFormatOptions.length > 0) {
      const currentFormat = current.format;
      const stillValid = currentFormat && newFormatOptions.some((o) => o.value === currentFormat);
      patch.format = stillValid ? currentFormat : defaultFormatForType(newType);
    } else {
      patch.format = undefined;
    }
    const updated = fields.map((f, i) => {
      if (i !== index) return f;
      const next = { ...f, ...patch };
      if (next.format === undefined) delete next.format;
      return next;
    });
    onFieldsChange(updated);
  };

  const handleCopyFormatToSiblings = (index) => {
    const source = fields[index];
    const targetFormat = source.format || defaultFormatForType(source.type);
    if (!targetFormat) return;
    const updated = fields.map((f, i) => {
      if (i === index) return f;
      if (f.type !== source.type) return f;
      return { ...f, format: targetFormat };
    });
    onFieldsChange(updated);
  };

  return (
    <div className={styles.container}>
      <strong className={styles.title}>🔧 API Variables (auto-detected) (from API configuration):</strong>

      {fields.length === 0 && <p className={styles.emptyMessage}>No variables detected. They will appear as you add placeholders to the API config above.</p>}

      {fields.map((field, idx) => {
        // Data type format
        const formatOptions = formatOptionsForType(field.type);
        const hasFormat = Array.isArray(formatOptions) && formatOptions.length > 0;

        const siblingsOfSameType = fields.filter((f, i) => i !== idx && f.type === field.type);
        const siblingFormatsDiffer = hasFormat && siblingsOfSameType.length > 0 && siblingsOfSameType.some((f) => (f.format || "") !== (field.format || ""));

        const attachedComponent = field.component_id ? library.find((c) => c.component_id === field.component_id) : null;
        const isLibraryManaged = !!field.component_id;
        const isDangling = isLibraryManaged && !attachedComponent && !field.pinned;

        return (
          <div key={field.field_name} className={styles.fieldRow}>
            <div className={styles.fieldNameWrapper}>
              <label className={styles.fieldLabel}>Field Name</label>
              <input type="text" value={field.field_name} readOnly className={`${styles.fieldInput} ${styles.readOnlyInput}`} />
            </div>

            <div className={styles.fieldLabelWrapper}>
              <label className={styles.fieldLabel}>Label</label>
              <input type="text" value={field.label} onChange={(e) => handleFieldChange(idx, "label", e.target.value)} disabled={isConfigured} className={`${styles.fieldInput} ${isConfigured ? styles.disabledInput : ""}`} />
            </div>

            <div className={styles.fieldTypeWrapper}>
              <label className={styles.fieldLabel} style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                Type
                <span onMouseEnter={() => setShowTypeHelp(true)} onMouseLeave={() => setShowTypeHelp(false)} style={{ position: "relative", display: "inline-flex", alignItems: "center", cursor: "help", color: "#667eea" }}>
                  <FaInfoCircle size={11} />
                  {showTypeHelp && (
                    <div style={{ position: "absolute", bottom: "calc(100% + 8px)", left: "50%", transform: "translateX(-50%)", backgroundColor: "#2d3748", color: "white", padding: "10px 12px", borderRadius: "6px", fontSize: "11px", width: "260px", textAlign: "left", zIndex: 100, boxShadow: "0 4px 12px rgba(0,0,0,0.25)", lineHeight: 1.5, textTransform: "none", letterSpacing: "normal", fontWeight: 400, pointerEvents: "none" }}>
                      <strong style={{ display: "block", marginBottom: "4px", color: "#90cdf4" }}>Why field type matters</strong>
                      The type is used on the client to render the correct input widget (date picker, number field, email validation, etc.) and to validate the user's answer before it reaches your API. A wrong type produces a wrong-looking form and can break the conversation flow.
                      <div style={{ marginTop: "8px", paddingTop: "6px", borderTop: "1px solid #4a5568", fontSize: "10px", color: "#cbd5e0" }}>
                        {Object.entries(TYPE_HINTS).map(([k, v]) => (
                          <div key={k} style={{ display: "flex", justifyContent: "space-between", gap: "8px", marginTop: "2px" }}>
                            <code style={{ color: "#f6ad55", fontFamily: "monospace", fontSize: "10px" }}>{k}</code>
                            <span style={{ flex: 1, textAlign: "right", color: "#a0aec0" }}>{v}</span>
                          </div>
                        ))}
                      </div>
                      <div style={{ position: "absolute", top: "100%", left: "50%", transform: "translateX(-50%)", border: "6px solid transparent", borderTopColor: "#2d3748" }} />
                    </div>
                  )}
                </span>
              </label>
              <select value={field.type} onChange={(e) => handleTypeChange(idx, e.target.value)} disabled={isConfigured || readOnly || isLibraryManaged} className={`${styles.fieldSelect} ${isConfigured || readOnly || isLibraryManaged ? styles.disabledInput : ""}`} title={isLibraryManaged ? "Data type is controlled by the imported library component. Detach to edit." : ""}>
                {FIELD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>

            {/* Format dropdown — based on the Data Type */}
            {hasFormat && (
              <div className={styles.fieldFormatWrapper}>
                <label className={styles.fieldLabel} style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  Format
                  <span onMouseEnter={() => setShowFormatHelp(true)} onMouseLeave={() => setShowFormatHelp(false)} style={{ position: "relative", display: "inline-flex", alignItems: "center", cursor: "help", color: "#667eea" }}>
                    <FaInfoCircle size={11} />
                    {showFormatHelp && (
                      <div style={{ position: "absolute", bottom: "calc(100% + 8px)", left: "50%", transform: "translateX(-50%)", backgroundColor: "#2d3748", color: "white", padding: "10px 12px", borderRadius: "6px", fontSize: "11px", width: "260px", textAlign: "left", zIndex: 100, boxShadow: "0 4px 12px rgba(0,0,0,0.25)", lineHeight: 1.5, textTransform: "none", letterSpacing: "normal", fontWeight: 400, pointerEvents: "none" }}>
                        {FORMAT_TOOLTIP_INTRO}
                        <div style={{ position: "absolute", top: "100%", left: "50%", transform: "translateX(-50%)", border: "6px solid transparent", borderTopColor: "#2d3748" }} />
                      </div>
                    )}
                  </span>
                </label>
                <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                  <select value={field.format || defaultFormatForType(field.type)} onChange={(e) => handleFieldChange(idx, "format", e.target.value)} disabled={isConfigured || readOnly} className={`${styles.fieldSelect} ${isConfigured || readOnly ? styles.disabledInput : ""}`} style={{ flex: 1, minWidth: 0 }}>
                    {formatOptions.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                  </select>
                  {!isConfigured && !readOnly && siblingFormatsDiffer && (
                    <button type="button" onClick={() => handleCopyFormatToSiblings(idx)} title={`Copy "${field.format || defaultFormatForType(field.type)}" to all other ${prettyTypeName(field.type)} fields in this form`} style={{ padding: "4px 8px", fontSize: "11px", whiteSpace: "nowrap", backgroundColor: "#667eea", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" }}>
                      Copy to all {prettyTypeName(field.type)}
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* ⭐ NEW: Widget Type dropdown — ONLY shown for library-managed fields */}
            {isLibraryManaged && (
              <div className={styles.fieldTypeWrapper}>
                <label className={styles.fieldLabel} style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  Widget
                  <span onMouseEnter={() => setShowWidgetHelp(true)} onMouseLeave={() => setShowWidgetHelp(false)} style={{ position: "relative", display: "inline-flex", alignItems: "center", cursor: "help", color: "#667eea" }}>
                    <FaInfoCircle size={11} />
                    {showWidgetHelp && (
                      <div style={{ position: "absolute", bottom: "calc(100% + 8px)", left: "50%", transform: "translateX(-50%)", backgroundColor: "#2d3748", color: "white", padding: "10px 12px", borderRadius: "6px", fontSize: "11px", width: "260px", textAlign: "left", zIndex: 100, boxShadow: "0 4px 12px rgba(0,0,0,0.25)", lineHeight: 1.5, textTransform: "none", letterSpacing: "normal", fontWeight: 400, pointerEvents: "none" }}>
                        <strong style={{ display: "block", marginBottom: "4px", color: "#90cdf4" }}>Widget Type</strong>
                        This determines how the user interacts with the field (e.g., a dropdown vs a text box). It is controlled by the attached Library Component.
                        <div style={{ position: "absolute", top: "100%", left: "50%", transform: "translateX(-50%)", border: "6px solid transparent", borderTopColor: "#2d3748" }} />
                      </div>
                    )}
                  </span>
                </label>
                <select value={field.widget_type || field.type} disabled={true} className={`${styles.fieldSelect} ${styles.disabledInput}`} style={{ backgroundColor: "#f0f4ff", color: "#3a6fd4", fontWeight: "bold" }}>
                  {FIELD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            )}

            {hasLibraryFeature && (
              <div className={styles.fieldLibraryWrapper} style={{ gridColumn: "1 / -1", marginTop: 4, display: "flex", flexDirection: "column", gap: 4 }}>
                <LibraryAttachmentStrip field={field} attachedComponent={attachedComponent} isConfigured={isConfigured} readOnly={readOnly} onOpenPicker={() => onOpenPicker(field.field_name)} onReimport={() => onReimportFromLibrary(field.field_name)} onDetach={() => onDetachFromLibrary(field.field_name)} onRequestPinToggle={() => onRequestPinToggle(field.field_name)} pinnedSummary={field.pinned && field.pinned_snapshot ? <PinnedSnapshotSummary snapshot={field.pinned_snapshot} /> : null} />
                {isDangling && <div style={{ marginTop: 4, padding: "8px 10px", background: "#fff5f5", border: "1px solid #fed7d7", borderRadius: 4, fontSize: 11, color: "#c53030", lineHeight: 1.5 }}>⚠️ This field references a library component (<code>{field.component_id}</code>) that no longer exists in this app's library. The field will fail to render on the client. Either <strong>Re-import</strong> from a matching component, attach a different one, or <strong>Detach</strong> and set the type manually.</div>}
                {libraryLoading && !attachedComponent && field.component_id && <div style={{ marginTop: 4, fontSize: 11, color: "#718096" }}>⏳ Loading library…</div>}
              </div>
            )}

            <div className={styles.fieldRequiredWrapper}>
              <label className={styles.checkboxLabel}>
                <input type="checkbox" checked={field.required} onChange={(e) => handleFieldChange(idx, "required", e.target.checked)} disabled={isConfigured} className={styles.checkbox} />
                Required
              </label>
            </div>

            <div className={styles.fieldPlaceholderWrapper}>
              <label className={styles.fieldLabel}>Placeholder</label>
              <input type="text" value={field.placeholder} onChange={(e) => handleFieldChange(idx, "placeholder", e.target.value)} disabled={isConfigured} className={`${styles.fieldInput} ${isConfigured ? styles.disabledInput : ""}`} />
            </div>
          </div>
        );
      })}

      {formUuid && appName && (
        <ValidationRulesBuilder rules={validationRules} availableFields={(fields || []).map((f) => ({ field: f.field_name, label: f.label || f.field_name }))} onChange={handleRulesChange} loading={rulesLoading} readOnly={readOnly || isConfigured} />
      )}
    </div>
  );
};

export default InferredFieldsBuilder;